// client/legend-cms/src/predictionCampaigns/PredictionCampaignCreate.js
import React from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  BooleanInput,
  DateTimeInput,
  ImageInput,
  ImageField,
  ReferenceArrayInput,
  SelectArrayInput,
  required,
} from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

const useStyles = makeStyles({
  inline: {
    display: 'inline-block',
    marginLeft: '1em',
    '&.first-child': {
      marginLeft: 0,
    },
  },
  helperText: {
    width: '100%',
    marginBottom: 12,
    padding: '10px 12px',
    borderRadius: 8,
    backgroundColor: '#fff8e1',
    color: '#5d4037',
    fontSize: 13,
    fontWeight: 600,
  },
  sectionTitle: {
    width: '100%',
    marginTop: 24,
    marginBottom: 8,
    paddingBottom: 6,
    borderBottom: '1px solid #e0e0e0',
    color: '#292C33',
    fontSize: 15,
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  newTeamLink: {
    width: '100%',
    marginBottom: 12,
    fontSize: 13,
    color: '#5d4037',
  },
});

const PredictionCampaignCreate = (props) => {
  const classes = useStyles();

  return (
    <Create title="Cadastrar campanha de palpites" {...props}>
      <SimpleForm>
        <div className={classes.helperText}>
          Só pode existir uma campanha ativa por vez. Ao marcar "Ativa" aqui,
          qualquer outra campanha ativa será desativada automaticamente.
        </div>

        <BooleanInput source="active" label="Ativa" defaultValue={false} />

        <div />

        <TextInput
          source="slug"
          label="Slug (identificador único)"
          helperText="Ex: copa-2026, temporada-2027. Não pode repetir."
          validate={required()}
          formClassName={clsx(classes.inline, 'first-child')}
        />

        <TextInput
          source="title"
          label="Título"
          validate={required()}
          formClassName={classes.inline}
        />

        <div />

        <TextInput source="subtitle" label="Subtítulo" fullWidth />

        <div />

        <TextInput
          source="card_title"
          label="Título do card (Galeria)"
          formClassName={clsx(classes.inline, 'first-child')}
        />

        <TextInput
          source="card_description"
          label="Descrição do card (Galeria)"
          formClassName={classes.inline}
        />

        <div />

        <ImageInput
          source="card_image"
          label="Imagem do card (Galeria)"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          helperText="jpg, png ou webp. Se vazio, usa o asset padrão do app."
        >
          <ImageField source="src" title="title" />
        </ImageInput>

        <ImageInput
          source="intro_image"
          label="Imagem da introdução"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          helperText="jpg, png ou webp. Se vazio, usa o asset padrão do app."
        >
          <ImageField source="src" title="title" />
        </ImageInput>

        <ImageInput
          source="background_image"
          label="Imagem de fundo"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          helperText="jpg, png ou webp. Se vazio, usa o asset padrão do app."
        >
          <ImageField source="src" title="title" />
        </ImageInput>

        <div />

        <TextInput
          source="primary_color"
          label="Cor primária"
          helperText="Hex, ex: #00E676. Usada nos destaques dos textos."
          formClassName={clsx(classes.inline, 'first-child')}
        />

        <TextInput
          source="secondary_color"
          label="Cor secundária"
          helperText="Hex, ex: #292C33."
          formClassName={classes.inline}
        />

        <div />

        <TextInput
          source="intro_title"
          label="Título da introdução"
          fullWidth
        />

        <TextInput
          source="intro_description"
          label="Descrição da introdução"
          multiline
          fullWidth
        />

        <TextInput
          source="intro_button_text"
          label="Texto do botão da introdução"
        />

        <div className={classes.helperText}>
          Título hero da introdução (2 linhas + ano), usado apenas quando
          "Imagem da introdução" está vazia. Em branco, o app usa o texto
          traduzido padrão ("PALPITE" / "MUNDIAL" / "2026").
        </div>

        <TextInput
          source="intro_title_line1"
          label="Título hero — linha 1"
          formClassName={clsx(classes.inline, 'first-child')}
        />

        <TextInput
          source="intro_title_line2"
          label="Título hero — linha 2"
          formClassName={classes.inline}
        />

        <TextInput
          source="intro_year_text"
          label="Título hero — ano"
          formClassName={classes.inline}
        />

        <div />

        <TextInput
          source="feedback_title"
          label="Título do feedback (pós-envio)"
          formClassName={clsx(classes.inline, 'first-child')}
        />

        <TextInput
          source="feedback_description"
          label="Descrição do feedback (pós-envio)"
          multiline
          formClassName={classes.inline}
        />

        <div />

        <TextInput source="ranking_title" label="Título do ranking" />

        <div className={classes.sectionTitle}>Textos da tela de palpites</div>

        <div className={classes.helperText}>
          Todos os campos abaixo são opcionais: em branco, o app usa o texto
          padrão traduzido. Nos campos longos dá para usar **negrito** e
          ==destaque== (o destaque sai na cor primária da campanha).
        </div>

        <TextInput
          source="how_it_works_title"
          label='Título "Como funciona"'
          helperText='Aparece na tela de introdução. Ex: "COMO FUNCIONA".'
          fullWidth
        />

        <TextInput
          source="how_it_works_description"
          label='Texto "Como funciona"'
          helperText="Aceita **negrito** e ==destaque==. As quebras de linha são preservadas."
          multiline
          fullWidth
        />

        <div />

        <TextInput
          source="matches_section_title"
          label="Título da seção de jogos"
          helperText='Ex: "JOGOS DO BRASIL", "RODADA 1".'
          formClassName={clsx(classes.inline, 'first-child')}
        />

        <TextInput
          source="top_four_section_title"
          label="Título da seção do Top 4"
          helperText='Ex: "MEU TOP 4".'
          formClassName={classes.inline}
        />

        <div />

        <TextInput
          source="top_four_first_label"
          label="Rótulo da 1ª colocação"
          formClassName={clsx(classes.inline, 'first-child')}
        />

        <TextInput
          source="top_four_second_label"
          label="Rótulo da 2ª colocação"
          formClassName={classes.inline}
        />

        <TextInput
          source="top_four_third_label"
          label="Rótulo da 3ª colocação"
          formClassName={classes.inline}
        />

        <TextInput
          source="top_four_fourth_label"
          label="Rótulo da 4ª colocação"
          formClassName={classes.inline}
        />

        <div className={classes.helperText}>
          Os subtítulos das colocações só aparecem no app quando preenchidos.
          Deixe em branco para exibir somente o rótulo.
        </div>

        <TextInput
          source="top_four_first_subtitle"
          label="Subtítulo da 1ª colocação"
          formClassName={clsx(classes.inline, 'first-child')}
        />

        <TextInput
          source="top_four_second_subtitle"
          label="Subtítulo da 2ª colocação"
          formClassName={classes.inline}
        />

        <TextInput
          source="top_four_third_subtitle"
          label="Subtítulo da 3ª colocação"
          formClassName={classes.inline}
        />

        <TextInput
          source="top_four_fourth_subtitle"
          label="Subtítulo da 4ª colocação"
          formClassName={classes.inline}
        />

        <div />

        <TextInput
          source="top_four_note"
          label="Aviso do Top 4"
          helperText='Ex: "IMPORTANTE: Nenhum time pode ser repetido."'
          multiline
          fullWidth
        />

        <div className={classes.sectionTitle}>Regras da campanha</div>

        <BooleanInput
          source="top_four_enabled"
          label="Habilitar Top 4"
          defaultValue={true}
          formClassName={clsx(classes.inline, 'first-child')}
        />

        <BooleanInput
          source="ranking_enabled"
          label="Habilitar Ranking"
          defaultValue={true}
          formClassName={classes.inline}
        />

        <div />

        <ReferenceArrayInput
          source="top_four_team_ids"
          reference="prediction-teams"
          label="Times elegíveis para o Top 4"
          filter={{ active: true }}
          sort={{ field: 'name', order: 'ASC' }}
          perPage={500}
          helperText="Se vazio, o seletor de Top 4 mostra todos os times ativos."
          fullWidth
        >
          <SelectArrayInput optionText="name" />
        </ReferenceArrayInput>

        <div className={classes.newTeamLink}>
          <a
            href="#/prediction-teams/create"
            target="_blank"
            rel="noopener noreferrer"
          >
            Cadastrar novo time
          </a>{' '}
          — abre em outra aba. Depois de salvar o time, recarregue esta página
          para ele aparecer na lista acima.
        </div>

        <div />

        <DateTimeInput
          source="starts_at"
          label="Início (opcional)"
          formClassName={clsx(classes.inline, 'first-child')}
        />

        <DateTimeInput
          source="ends_at"
          label="Fim (opcional)"
          formClassName={classes.inline}
        />
      </SimpleForm>
    </Create>
  );
};

export default PredictionCampaignCreate;
